import { BotInputAdapter } from "./botInputAdapter";
import type { BotDecisionDiagnostics } from "./botInputAdapter";
import { createBotIdentity, type BotIdentity } from "./botIdentity";
import { createBotSkillProfile, type BotSkillProfile } from "./botSkillProfile";
import type { HeartbeatPayload } from "./types";

export class BotPlayer {
  readonly identity: BotIdentity;
  readonly skillProfile: BotSkillProfile;
  readonly input: BotInputAdapter;
  private heartbeatTimer = 0;
  private heartbeatSeq = 0;
  private fakeScore = 0;
  private fakeLevel = 1;

  constructor(
    identity: BotIdentity = createBotIdentity(),
    skillProfile: BotSkillProfile = createBotSkillProfile({})
  ) {
    this.identity = identity;
    this.skillProfile = skillProfile;
    this.fakeLevel = this.identity.level;

    this.input = new BotInputAdapter(this.skillProfile);
  }

  /** Generate a fake heartbeat for the opponent stats display. */
  getHeartbeat(): HeartbeatPayload {
    this.heartbeatSeq += 1;
    const levelProgressPct = Math.min(1, (this.fakeScore % 1000) / 1000);
    return {
      heartbeatSeq: this.heartbeatSeq,
      ackGameplaySeq: 0,
      score: this.fakeScore,
      level: this.fakeLevel,
      levelProgressPct,
      equippedSkin: this.identity.equippedSkin,
      doomPct: 0.5 + Math.random() * 0.4,
      groovePct: Math.random() * 0.8,
      grooveActive: Math.random() < 0.12,
      grooveReady: Math.random() < 0.25,
      pendingRotation: Math.random() < 0.5 ? "LEFT" : "RIGHT",
      rotationKeys: Math.floor(Math.random() * 4),
      cash: Math.floor(this.fakeScore / 200),
      powerups: {
        HINT: Math.floor(Math.random() * 2),
        ROWRIP: Math.floor(Math.random() * 3),
        COLCUT: Math.floor(Math.random() * 3),
        TRANSMUTE: Math.floor(Math.random() * 3),
        ZIGZAGBOLT: Math.floor(Math.random() * 2)
      }
    };
  }

  /** Call each frame to simulate bot score progression. */
  updateFakeStats(dt: number): void {
    this.heartbeatTimer += dt;
    // Slowly increment fake score
    this.fakeScore += Math.floor(dt * (30 + Math.random() * 20));
    if (this.fakeScore > this.fakeLevel * 500) {
      this.fakeLevel = Math.min(20, this.fakeLevel + 1);
    }
  }

  getDecisionDiagnostics(): BotDecisionDiagnostics | null {
    return this.input.getLastDecisionDiagnostics();
  }
}
